import React, { useEffect, useRef, useState } from 'react';
import { formatMoney, spinLandCue } from '../game/presentation';
import { useJuice } from '../context/JuiceContext';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface SlotMachineProps {
  isSpinning: boolean;
  spinValue: number | string;
  reelValues: number[] | null;
  specialValue: string | null;
  onSpin: () => void;
  onLanded?: () => void;
  isMyTurn: boolean;
  gameEnded: boolean;
}

const SPECIALS = ['$$$', 'BANKRUPT', 'LOSE TURN'];

const SlotMachine: React.FC<SlotMachineProps> = ({
  isSpinning,
  spinValue,
  reelValues,
  specialValue,
  onSpin,
  onLanded,
  isMyTurn,
  gameEnded,
}) => {
  const { play } = useJuice();
  const reducedMotion = usePrefersReducedMotion();
  const [digits, setDigits] = useState<number[]>(reelValues ?? [0, 0, 0, 0]);
  const [special, setSpecial] = useState(specialValue ?? '$$$');
  const [spinningMask, setSpinningMask] = useState([false, false, false, false, false]);
  const frame = useRef<number | null>(null);
  const lastTick = useRef(0);
  const onLandedRef = useRef(onLanded);
  const playRef = useRef(play);
  onLandedRef.current = onLanded;
  playRef.current = play;

  const reelKey = `${reelValues?.join('') ?? ''}|${specialValue ?? ''}`;

  useEffect(() => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }

    if (!isSpinning) {
      if (reelValues) setDigits(reelValues);
      if (specialValue) setSpecial(specialValue);
      setSpinningMask([false, false, false, false, false]);
      return undefined;
    }

    const finish = () => {
      if (reelValues) setDigits(reelValues);
      if (specialValue) setSpecial(specialValue);
      setSpinningMask([false, false, false, false, false]);
      playRef.current(spinLandCue(specialValue));
      onLandedRef.current?.();
    };

    if (reducedMotion) {
      finish();
      return undefined;
    }

    setSpinningMask([true, true, true, true, true]);
    playRef.current('spin');
    const started = performance.now();
    const stopAt = [720, 980, 1240, 1500, 1880];
    lastTick.current = started;

    const tick = (now: number) => {
      const still = stopAt.map((time) => now - started < time);
      if (now - lastTick.current > 140) {
        playRef.current('tick');
        lastTick.current = now;
      }
      setDigits((prev) =>
        prev.map((digit, index) => {
          if (!still[index] && reelValues) return reelValues[index];
          return still[index] ? Math.floor(Math.random() * 10) : digit;
        })
      );
      setSpecial((prev) => {
        if (!still[4]) return specialValue ?? prev;
        return SPECIALS[Math.floor(Math.random() * SPECIALS.length)];
      });
      setSpinningMask(still);
      if (still.some(Boolean)) {
        frame.current = requestAnimationFrame(tick);
      } else {
        finish();
      }
    };

    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current) {
        cancelAnimationFrame(frame.current);
      }
    };
    // reelKey captures reelValues + specialValue identity without array-ref churn
  }, [isSpinning, reelKey, reducedMotion, reelValues, specialValue]);

  const canPull = isMyTurn && !gameEnded && !isSpinning;
  const resultLabel = (() => {
    if (isSpinning) return 'Spinning…';
    if (typeof spinValue === 'number' && spinValue > 0) return formatMoney(spinValue);
    if (typeof spinValue === 'string' && spinValue) return spinValue;
    if (specialValue && specialValue !== '$$$') return specialValue;
    return canPull ? 'Pull to spin' : '';
  })();
  const resultBad = resultLabel === 'BANKRUPT' || resultLabel === 'LOSE TURN';

  return (
    <div className="ss-slots">
      <div className="ss-machine" aria-hidden="true">
        <div className="ss-reels">
          {digits.map((digit, index) => (
            <div key={`d-${index}`} className={`ss-reel${spinningMask[index] ? ' is-spinning' : ''}`}>
              <span className="ss-reel-face">{digit}</span>
            </div>
          ))}
          <div className={`ss-reel is-special${spinningMask[4] ? ' is-spinning' : ''}`}>
            <span className="ss-reel-face">{special}</span>
          </div>
        </div>
        <button
          type="button"
          className="ss-lever"
          onClick={() => canPull && onSpin()}
          disabled={!canPull}
          aria-label="Pull the slot lever"
        >
          <span className="ss-lever-knob" />
          <span className="ss-lever-stick" />
        </button>
      </div>
      <div
        className={`ss-spin-result${resultBad ? ' is-bad' : ''}${resultLabel === 'Pull to spin' ? ' is-hint' : ''}`}
        aria-live="polite"
      >
        {resultLabel}
      </div>
      {canPull && (
        <button type="button" className="ss-spin-btn" onClick={onSpin}>
          Spin
        </button>
      )}
    </div>
  );
};

export default SlotMachine;
